import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import KPICard from '../components/KPICard';
import StatusBadge from '../components/StatusBadge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Lightbulb, 
  FolderKanban, 
  Clock, 
  CheckCircle2, 
  TrendingUp,
  Plus,
  ArrowRight,
  Loader2
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const COLORS = ['#235894', '#26B8A7', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [ideasByStatus, setIdeasByStatus] = useState([]);
  const [projectsByStatus, setProjectsByStatus] = useState([]);
  const [recentIdeas, setRecentIdeas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, ideasStatusRes, projectsStatusRes, ideasRes] = await Promise.all([
        api.get('/reporting/dashboard'),
        api.get('/reporting/ideas-by-status'),
        api.get('/reporting/projects-by-status'),
        api.get('/ideas'),
      ]);
      
      setStats(statsRes.data);
      setIdeasByStatus(ideasStatusRes.data);
      setProjectsByStatus(projectsStatusRes.data);
      setRecentIdeas(ideasRes.data.slice(0, 5));
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-[#235894]" />
      </div>
    );
  }

  const formatCurrency = (value) => {
    if (!value) return '£0';
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(value);
  };

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">
            Welcome back, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-slate-500 mt-1">
            Here's what's happening with your CI initiatives
          </p>
        </div>
        <Button 
          onClick={() => navigate('/ideas/new')}
          className="bg-[#235894] hover:bg-[#1E4B80]"
          data-testid="submit-idea-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Submit New Idea
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <KPICard
          title="Ideas Submitted"
          value={stats?.ideas?.submitted || 0}
          subtitle={`${stats?.ideas?.total || 0} total ideas`}
          icon={Lightbulb}
          onClick={() => navigate('/ideas?status=Submitted')}
        />
        <KPICard
          title="Under Review"
          value={stats?.ideas?.under_review || 0}
          subtitle="Awaiting approval"
          icon={Clock}
          onClick={() => navigate('/ideas?status=Under Review')}
        />
        <KPICard
          title="Projects Active"
          value={stats?.projects?.in_progress || 0}
          subtitle={`${stats?.projects?.completed || 0} completed`}
          icon={FolderKanban}
          onClick={() => navigate('/projects?status=In Progress')}
        />
        <KPICard
          title="Benefits Realised"
          value={formatCurrency(stats?.benefits?.realised)}
          subtitle={`${formatCurrency(stats?.benefits?.expected)} expected`}
          icon={TrendingUp}
          trend="up"
          trendValue="+12% this month"
          onClick={() => navigate('/reporting')}
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Ideas by Status */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Ideas by Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={ideasByStatus} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis type="number" />
                  <YAxis dataKey="status" type="category" width={100} tick={{ fontSize: 12 }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #E2E8F0',
                      borderRadius: '8px'
                    }} 
                  />
                  <Bar dataKey="count" fill="#235894" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Projects by Status */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Projects by Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64 flex items-center justify-center">
              {projectsByStatus.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={projectsByStatus}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="count"
                      nameKey="status"
                      label={({ status, count }) => `${status}: ${count}`}
                    >
                      {projectsByStatus.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-slate-400">No project data available</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Ideas */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-semibold">Recent Ideas</CardTitle>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate('/ideas')}
            data-testid="view-all-ideas-btn"
          >
            View All
            <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {recentIdeas.length === 0 ? (
              <p className="text-slate-400 text-center py-8">No ideas submitted yet</p>
            ) : (
              recentIdeas.map((idea) => (
                <div 
                  key={idea.id}
                  className="flex items-center justify-between p-4 rounded-lg border border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/ideas/${idea.id}`)}
                  data-testid={`recent-idea-${idea.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-slate-900 truncate">{idea.title}</h4>
                    <p className="text-sm text-slate-500 truncate">{idea.department} • {idea.submitter_name}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <StatusBadge status={idea.idea_type} />
                    <StatusBadge status={idea.status} />
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
